import type { Case } from "./useRealtimeCases";
import { formatParisDate, formatParisTime } from "./parisDateTime";
import { BRAND_CONFIG, detectBrandFromCenter } from "./brandConfig";

/** Prestations particulières proposées pendant l'appel (script v2, Paul 04/11/2026). */
export const SPECIAL_SERVICES = [
  ["dj", "DJ"],
  ["photobooth", "Photobooth"],
  ["music", "Musique"],
  ["privatization", "Privatisation / espace dédié"],
  ["barman", "Barman"],
  ["animation", "Animation"],
  ["other", "Autre"],
  ["none", "Aucun"],
] as const;
export type SpecialService = (typeof SPECIAL_SERVICES)[number][0];
export const SPECIAL_SERVICE_LABELS = Object.fromEntries(SPECIAL_SERVICES) as Record<SpecialService, string>;

/** Formulation orale des prestations dans la reformulation de fin d'appel. */
const SPECIAL_SERVICE_SPOKEN: Record<SpecialService, string> = {
  dj: "un DJ",
  photobooth: "un photobooth",
  music: "de la musique",
  privatization: "une privatisation ou un espace dédié",
  barman: "un barman",
  animation: "une animation",
  other: "",
  none: "",
};

export type BudgetTaxBasis = "ht" | "ttc" | "unspecified";
export const BUDGET_TAX_LABELS: Record<BudgetTaxBasis, string> = { ht: "HT", ttc: "TTC", unspecified: "Non précisé" };
export type Drinks = "soft" | "alcohol";
export const DRINKS_LABELS: Record<Drinks, string> = { soft: "Soft uniquement", alcohol: "Avec alcool" };

/**
 * Suivi d'appel (table case_call_guides). Script v2 : on garde les 3 oui, l'objectif et les notes ;
 * on ajoute HT/TTC, restauration, boissons et prestations particulières.
 * Les anciens champs (priorité du client, confirmation du budget, décideur, date de décision,
 * recommandation) restent en base mais ne sont plus affichés.
 */
export type CallGuide = {
  three_yes_validated: boolean;
  event_goal: string | null;
  budget_tax_basis: BudgetTaxBasis | null;
  catering: string | null;
  drinks: Drinks | null;
  catering_notes: string | null;
  special_services: SpecialService[];
  special_services_notes: string | null;
  // Anciens champs, conservés en lecture seulement.
  budget_confirmation?: "confirmed" | "adjusted" | "not_applicable" | null;
  client_driver?: "image" | "cohesion" | "fun" | "budget" | null;
  expert_recommendation?: boolean;
  decision_maker?: string | null;
  decision_date?: string | null;
};
export const EMPTY_CALL_GUIDE: CallGuide = {
  three_yes_validated: false,
  event_goal: null,
  budget_tax_basis: null,
  catering: null,
  drinks: null,
  catering_notes: null,
  special_services: [],
  special_services_notes: null,
};
export const euros = (amount: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(amount);
const eurosRounded = (amount: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);

/** « a », « a et b », « a, b et c ». */
export function listFr(items: readonly string[]): string {
  const clean = items.map((item) => item.trim()).filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? "";
  return `${clean.slice(0, -1).join(", ")} et ${clean[clean.length - 1]}`;
}

/** Heure dite à voix haute : « 18 h 30 », « 19 h ». */
function spokenTime(hhmm: string | null): string | null {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":");
  return m && m !== "00" ? `${Number(h)} h ${m}` : `${Number(h)} h`;
}

const positive = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/** Prestations choisies, « Aucun » exclu. */
export function chosenServices(guide: Pick<CallGuide, "special_services">): SpecialService[] {
  return (guide.special_services || []).filter((s) => s !== "none" && s in SPECIAL_SERVICE_LABELS);
}

/** « Budget : 2 000,00 € TTC au total » (HT/TTC seulement s'il a été précisé pendant l'appel). */
function budgetLine(c: Case, basis: BudgetTaxBasis | null) {
  const tax = basis && basis !== "unspecified" ? ` ${BUDGET_TAX_LABELS[basis]}` : "";
  if (positive(c.budget)) return `Budget : ${euros(c.budget!)}${tax} au total`;
  if (positive(c.budget_per_person)) return `Budget : ${euros(c.budget_per_person!)}${tax} par personne`;
  return null;
}

export function buildCallRecap(c: Case, guide: CallGuide) {
  const context = getCallScriptContext(c, "");
  const services = chosenServices(guide);
  return [
    c.event_date ? `Événement : ${context.eventDate}${context.eventTime ? ` à ${context.eventTime}` : " (heure à préciser)"}` : null,
    context.count != null ? `Participants : ${context.count}` : null,
    c.activities?.length ? `Activités : ${c.activities.join(", ")}` : null,
    c.extras?.length ? `Extras de la demande : ${c.extras.join(", ")}` : null,
    guide.three_yes_validated ? "Les 3 oui : validés" : null,
    budgetLine(c, guide.budget_tax_basis),
    c.budget != null && c.budget_per_person != null ? `Budget par personne : ${euros(c.budget_per_person)}` : null,
    guide.event_goal?.trim() ? `Objectif de l’événement : ${guide.event_goal.trim()}` : null,
    guide.catering?.trim() ? `Restauration : ${guide.catering.trim()}` : null,
    guide.drinks ? `Boissons : ${DRINKS_LABELS[guide.drinks]}` : null,
    guide.catering_notes?.trim() ? `Précisions restauration et boissons : ${guide.catering_notes.trim()}` : null,
    services.length
      ? `Prestations particulières : ${services.map((s) => SPECIAL_SERVICE_LABELS[s]).join(", ")}`
      : guide.special_services?.includes("none") ? "Prestations particulières : aucune" : null,
    guide.special_services_notes?.trim() ? `Précisions prestations : ${guide.special_services_notes.trim()}` : null,
    c.phone_reminder_date ? `Relance prévue : ${formatParisDate(`${c.phone_reminder_date}T12:00:00Z`)}${c.phone_reminder_time ? ` à ${c.phone_reminder_time.slice(0, 5)}` : ""}` : null,
  ].filter(Boolean).join("\n");
}

/** Phrases du script d'appel v2, préremplies avec le dossier. Aucune variable ne reste visible si l'info manque. */
export function getCallScriptContext(c: Case, salesperson: string) {
  const count = c.participants ?? c.pax;
  const brandName = BRAND_CONFIG[c.brand || detectBrandFromCenter(c.center)].name;
  const center = c.center?.trim() || "";
  const brandAndCenter = !center ? brandName : center.toLowerCase().startsWith(brandName.toLowerCase()) ? center : `${brandName} ${center}`;
  const dateKnown = !!c.event_date && Number.isFinite(Date.parse(c.event_date));
  const eventDate = dateKnown
    ? formatParisDate(c.event_date!, { day: "numeric", month: "long", year: "numeric" })
    : "date à préciser";
  const eventTime =
    dateKnown && c.event_time_provided === true ? formatParisTime(c.event_date!) : null;
  const budget = positive(c.budget)
    ? `${euros(c.budget!)} au total`
    : positive(c.budget_per_person)
      ? `${euros(c.budget_per_person!)} par personne`
      : null;
  const budgetSpoken = positive(c.budget)
    ? eurosRounded(c.budget!)
    : positive(c.budget_per_person)
      ? `${eurosRounded(c.budget_per_person!)} par personne`
      : null;
  const when = dateKnown ? `le ${eventDate}${eventTime ? ` à ${spokenTime(eventTime)}` : ""}` : null;
  const forWhom = count != null && count > 0 ? `pour ${count} personnes` : null;
  const firstName = c.client_first_name?.trim();
  const activities = (c.activities || []).filter(Boolean);
  const reminderDate = c.phone_reminder_date
    ? formatParisDate(`${c.phone_reminder_date}T12:00:00Z`, { day: "numeric", month: "long" })
    : null;
  const reminderTime = c.phone_reminder_time?.slice(0, 5) || null;
  return {
    count,
    budget,
    brandName,
    eventDate,
    eventTime,
    when,
    forWhom,
    greeting: `Bonjour${firstName ? ` ${firstName}` : ""}, ${salesperson ? `${salesperson} pour` : "ici"} ${brandAndCenter}.`,
    availability:
      "Je vous appelle concernant votre demande pour l’organisation de votre événement d’entreprise. Est-ce que vous avez 5 petites minutes à m’accorder pour qu’on puisse faire le point sur votre projet et répondre à vos éventuelles questions ?",
    confirmation:
      when || forWhom
        ? `Super, merci. Tout d’abord, je voulais simplement confirmer avec vous : votre demande concerne bien ${[when, forWhom].filter(Boolean).join(", ")}, c’est bien ça ?`
        : "Super, merci. Tout d’abord, pouvez-vous me rappeler la date de votre événement et le nombre de participants ?",
    start:
      "Parfait. Je vais simplement reprendre quelques points avec vous pour bien comprendre votre projet et vous préparer la proposition la plus adaptée.",
    budgetQuestion: budgetSpoken
      ? `Je voulais également confirmer votre budget. Vous aviez indiqué ${budgetSpoken}. Est-ce que ce montant est en HT ou en TTC ?`
      : "Est-ce que vous avez déjà une enveloppe en tête pour votre événement ? Plutôt en HT ou en TTC ?",
    budgetFollowUp: budgetSpoken ? "Et on reste bien sur cette enveloppe pour votre événement ?" : null,
    discovery:
      "Très bien. Avant de vous faire notre proposition, j’aimerais simplement mieux comprendre votre projet. Quel est l’objectif principal de cet événement ? Est-ce que vous avez quelques informations à me communiquer à ce sujet ?",
    activitiesQuestion: activities.length
      ? `Vous aviez sélectionné ${listFr(activities)}. C’est bien ${activities.length > 1 ? "ce que vous souhaitez faire" : "l’activité que vous souhaitez faire"} avec votre groupe ?`
      : "Quelle activité aimeriez-vous faire avec votre groupe ?",
    cateringQuestion:
      "Et concernant la restauration, vous aviez déjà quelque chose en tête ou vous souhaitez que je vous recommande le format le plus adapté à votre événement ?",
    drinksQuestion:
      "Et pour les boissons, vous souhaitez plutôt partir sur du soft uniquement ou prévoir également de l’alcool ?",
    extrasQuestion:
      "Est-ce que vous avez prévu des prestations ou besoins particuliers en complément ? Par exemple un DJ, un photobooth, de la musique, une privatisation, un barman… ?",
    proposal: "Je vais reprendre tout ça et vous préparer une proposition d’organisation adaptée à votre événement.",
    followUp:
      reminderDate && reminderTime
        ? `Je vous envoie la proposition rapidement. On se rappelle donc le ${reminderDate} à ${spokenTime(reminderTime)} pour faire le point ensemble ?`
        : "Je vous envoie la proposition rapidement. Quel serait le meilleur moment pour vous rappeler et faire le point ensemble ?",
  };
}

/** Reformulation de fin d'appel : date, heure, participants, activités, restauration, boissons, extras. */
export function buildCallSummary(c: Case, guide: CallGuide): string {
  const context = getCallScriptContext(c, "");
  const head = [context.when, context.forWhom].filter((x): x is string => !!x).join(", ");
  const services = chosenServices(guide).map((s) => SPECIAL_SERVICE_SPOKEN[s]).filter(Boolean);
  const items = [
    (c.activities || []).filter(Boolean).length ? listFr((c.activities || []).filter(Boolean)) : null,
    guide.catering?.trim() ? `côté restauration ${guide.catering.trim()}` : null,
    guide.drinks === "soft" ? "des boissons soft uniquement" : guide.drinks === "alcohol" ? "des boissons avec alcool" : null,
    services.length ? `en complément ${listFr(services)}` : null,
  ].filter((x): x is string => !!x);
  const withItems = items.length ? `, avec ${listFr(items)}` : "";
  return `Parfait, merci pour toutes ces informations. Si je résume, vous recherchez donc une proposition pour ${head || "votre événement"}${withItems}.`;
}

export function latestActiveQuoteAmount(
  quotes: {
    id: string;
    version: number;
    created_at: string;
    amount_ht?: number | string | null;
    archived_at?: string | null;
  }[],
) {
  const latest = quotes
    .filter((q) => !q.archived_at)
    .sort(
      (a, b) =>
        b.version - a.version ||
        b.created_at.localeCompare(a.created_at) ||
        b.id.localeCompare(a.id),
    )[0];
  if (!latest || latest.amount_ht == null) return null;
  const amount = Number(latest.amount_ht);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}
